// DEV-60. Разбор исхода обновления из журнала FirmwareStatusNotification.
//
// Источник — `ПИВ_07` 0.14, раздел 1 («Обновление зарядных станций» и
// «Значения info при status: failed»). Сервер (`back_balance`,
// `utils/firmware-status.js`) кладёт разобранный исход третьим элементом
// записи журнала; здесь он читается, а если его нет (запись сделана до этой
// правки) — кадр разбирается тем же правилом на месте, чтобы старые записи
// не перестали опознаваться.
//
// Панель больше не ждёт только `transferred` и «finish to all ACC»: любой
// отказ — ESP, STM или станции — останавливает опрос и показывается
// оператору с номером станции и причиной.

import { UpdateLog } from "./types";

export type FirmwareOutcomeKind = "success" | "failure";
export type FirmwareComponent = "ESP" | "STM" | "AC_control";

export interface FirmwareOutcome {
  status: string | null;
  info: string | number | null;
  address: number | null;
  stationNumber: number | null;
  component: FirmwareComponent | null;
  reason: string | null;
  outcome: FirmwareOutcomeKind;
  final: boolean;
  known: boolean;
}

// Зеркало таблицы сервера: `info` -> компонент и причина (`ПИВ_07` 0.26, разд. 1;
// DEV-67.1). Совпадает с `back_balance/utils/firmware-status.js` побуквенно; сверка
// перечней — в `dev67.1-firmware-info-final.test.ts`.
export const FAILED_INFO: Record<string, { component: FirmwareComponent; reason: string }> =
  {
    "to STM_MDBN": { component: "STM", reason: "image_not_downloaded" },
    "to ACC": { component: "AC_control", reason: "image_not_downloaded" },
    "STM_MDBN:rejected_by_bootloader": {
      component: "STM",
      reason: "rejected_by_bootloader",
    },
    "STM_MDBN:map_revision_mismatch": {
      component: "STM",
      reason: "map_revision_mismatch",
    },
    "ACC:not_installed": { component: "AC_control", reason: "not_installed" },
    "ACC:no_answer": { component: "AC_control", reason: "no_answer" },
    "ACC:group_incomplete": {
      component: "AC_control",
      reason: "group_incomplete",
    },
    "ESP:unknown_image_kind": { component: "ESP", reason: "unknown_image_kind" },
    // DEV-67.1: значения протокола 0.24–0.26, на фронте отстававшие. Три общих
    // значения (`STM:image_too_large`, `ACC:image_too_large`, `STM:unknown_result`)
    // пришли и в `DEV-60`, и в `DEV-67.1`; хозяин — `DEV-67.1`, берём отсюда.
    "STM:image_too_large": { component: "STM", reason: "image_too_large" },
    "ACC:image_too_large": { component: "AC_control", reason: "image_too_large" },
    "ESP:image_too_large": { component: "ESP", reason: "image_too_large" },
    "STM:size_unknown": { component: "STM", reason: "size_unknown" },
    "ACC:size_unknown": { component: "AC_control", reason: "size_unknown" },
    "ESP:size_unknown": { component: "ESP", reason: "size_unknown" },
    "STM:unknown_result": { component: "STM", reason: "unknown_result" },
    "ESP:download_interrupted": { component: "ESP", reason: "download_interrupted" },
    "STM:download_interrupted": { component: "STM", reason: "download_interrupted" },
    "ACC:download_interrupted": {
      component: "AC_control",
      reason: "download_interrupted",
    },
    "ESP:download_failed": { component: "ESP", reason: "download_failed" },
    "STM:download_failed": { component: "STM", reason: "download_failed" },
    "ACC:download_failed": { component: "AC_control", reason: "download_failed" },
    "ESP:link_denied": { component: "ESP", reason: "link_denied" },
    "STM:link_denied": { component: "STM", reason: "link_denied" },
    "ACC:link_denied": { component: "AC_control", reason: "link_denied" },
    "ESP:no_update_partition": { component: "ESP", reason: "no_update_partition" },
    "ESP:write_failed": { component: "ESP", reason: "write_failed" },
    "STM:write_failed": { component: "STM", reason: "write_failed" },
    "ACC:write_failed": { component: "AC_control", reason: "write_failed" },
    "ESP:image_rejected": { component: "ESP", reason: "image_rejected" },
    "ESP:invalid_command": { component: "ESP", reason: "invalid_command" },
  };

// Числа 100 и 101 из разбора убраны (`ПИВ7-Q4`): это значения регистра 501 на
// стыке STM↔ESP, до сервера они не доходят. Значение вне перечня — «неизвестная
// причина»; исходный текст остаётся в `info`.

// DEV-67.1 (`ЗАГР-Q42`): «итоговость» кадра `failed` — по полю `address`, а не по
// перечню значений. Кадр без `address` завершает обход, с `address` — отказ одной
// станции. Перечень `FAILED_INFO` остаётся только для компонента и причины.
const PER_STATION_SUCCESS = /^finish to ACC № (\d+)$/;

function toNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "" && !isNaN(Number(value))) {
    return Number(value);
  }
  return null;
}

function componentFromInfo(info: unknown): FirmwareComponent | null {
  if (typeof info !== "string") return null;
  if (info.startsWith("ACC:")) return "AC_control";
  if (info.startsWith("STM_MDBN:")) return "STM";
  // DEV-67.1: значения вида `STM:image_too_large` — компонент STM.
  if (info.startsWith("STM:")) return "STM";
  if (info.startsWith("ESP:")) return "ESP";
  return null;
}

function parseMessage(message: string | object): unknown {
  if (typeof message === "string") {
    try {
      return JSON.parse(message);
    } catch {
      return null;
    }
  }
  return message;
}

/** Достаёт из записи журнала кадр и разобранный сервером исход, если он есть. */
export function extractFirmwareFrame(
  log: UpdateLog,
): { frame: Record<string, unknown>; parsed: FirmwareOutcome | null } | null {
  const parsedMessage = parseMessage(log?.message);
  if (!Array.isArray(parsedMessage)) return null;
  if (parsedMessage[0] !== "FirmwareStatusNotification") return null;

  const frame = parsedMessage[1];
  if (!frame || typeof frame !== "object" || Array.isArray(frame)) return null;

  const parsed = parsedMessage[2];
  if (
    parsed &&
    typeof parsed === "object" &&
    !Array.isArray(parsed) &&
    "outcome" in (parsed as Record<string, unknown>)
  ) {
    return { frame: frame as Record<string, unknown>, parsed: parsed as FirmwareOutcome };
  }

  return { frame: frame as Record<string, unknown>, parsed: null };
}

/** Разбирает кадр на месте — если серверной части исхода в записи нет. */
function classifyFrame(frame: Record<string, unknown>): FirmwareOutcome | null {
  const status = typeof frame.status === "string" ? frame.status : null;
  const info = frame.info === undefined ? null : frame.info;
  const address = toNumber(frame.address);

  const base: FirmwareOutcome = {
    status,
    info: info as string | number | null,
    address,
    stationNumber: null,
    component: null,
    reason: null,
    outcome: "success",
    final: false,
    known: false,
  };

  if (status === "failed") {
    const known = (typeof info === "string" && FAILED_INFO[info]) || null;

    return {
      ...base,
      outcome: "failure",
      component: known ? known.component : componentFromInfo(info),
      // Перечень закрытый (`ПИВ7-Q4`): значение вне перечня — «неизвестная
      // причина», а не молчание. Исходный текст остаётся в `info`.
      reason: known ? known.reason : "unknown",
      stationNumber: address,
      // DEV-67.1 (`ЗАГР-Q42`): итоговость — по полю `address`, а не по перечню.
      final: address === null,
      known: Boolean(known),
    };
  }

  if (status === "transferred") {
    if (info === "finish to all ACC") {
      return {
        ...base,
        outcome: "success",
        component: "AC_control",
        reason: "installed_all",
        final: true,
        known: true,
      };
    }

    const perStation = typeof info === "string" && info.match(PER_STATION_SUCCESS);
    if (perStation) {
      return {
        ...base,
        outcome: "success",
        component: "AC_control",
        reason: "installed",
        stationNumber: Number(perStation[1]),
        final: false,
        known: true,
      };
    }

    if (info === "to STM_MDBN") {
      return {
        ...base,
        outcome: "success",
        component: "STM",
        reason: "image_transferred",
        final: true,
        known: true,
      };
    }

    return { ...base, outcome: "success" };
  }

  if (status === "complete") {
    return {
      ...base,
      outcome: "success",
      component: "ESP",
      reason: "installed",
      final: true,
      known: true,
    };
  }

  return null;
}

/** Исход обновления из записи журнала — или null, если запись не исход. */
export function classifyFirmwareOutcome(log: UpdateLog): FirmwareOutcome | null {
  const extracted = extractFirmwareFrame(log);
  if (!extracted) return null;
  if (extracted.parsed) return extracted.parsed;
  return classifyFrame(extracted.frame);
}

/** Текст для оператора: что именно случилось и с какой станцией. */
export function describeFirmwareOutcome(outcome: FirmwareOutcome): string {
  const station =
    outcome.stationNumber === null || outcome.stationNumber === undefined
      ? null
      : outcome.stationNumber;

  if (outcome.outcome === "failure") {
    if (outcome.component === "AC_control") {
      if (outcome.reason === "not_installed") {
        return station === null
          ? "Станция не установила образ"
          : `Станция ${station} не установила образ`;
      }
      if (outcome.reason === "no_answer") {
        return station === null
          ? "Станция не отвечает"
          : `Станция ${station} не отвечает`;
      }
      if (outcome.reason === "group_incomplete") {
        return "Обход закончен: не все станции установили образ";
      }
      if (outcome.reason === "image_not_downloaded") {
        return "Образ для станции не скачался";
      }
    }

    if (outcome.component === "STM") {
      if (outcome.reason === "rejected_by_bootloader") {
        return "Загрузчик STM МДБН отверг образ — смотреть образ";
      }
      if (outcome.reason === "map_revision_mismatch") {
        return "Не совпала редакция карты памяти — нужен программатор";
      }
      if (outcome.reason === "image_not_downloaded") {
        return "Образ для STM МДБН не скачался";
      }
    }

    if (outcome.component === "ESP" && outcome.reason === "unknown_image_kind") {
      return "Ссылка на образ пришла в неизвестном поле";
    }

    // DEV-60 (возврат по `ПИВ7-Q4`): значение вне перечня — «неизвестная
    // причина»; исходный текст показываем рядом, чтобы отказ не терялся.
    if (outcome.reason === "unknown") {
      const raw =
        outcome.info === null || outcome.info === undefined
          ? "не названо"
          : String(outcome.info);
      return `Отказ обновления: неизвестная причина (info: ${raw})`;
    }

    const info = outcome.info === null ? "без значения info" : String(outcome.info);
    return `Отказ обновления: ${info}`;
  }

  if (outcome.component === "AC_control") {
    if (outcome.reason === "installed_all") return "Все станции подтвердили установку";
    if (outcome.reason === "installed") {
      return station === null
        ? "Станция подтвердила установку"
        : `Станция ${station} подтвердила установку`;
    }
  }
  if (outcome.component === "STM") return "Образ STM МДБН передан";
  if (outcome.component === "ESP") return "Обновление ESP завершено";

  return "Обновление завершено";
}
